import { Alert, Button, DrawerFooter, Icon, Label, Text } from "@assetwatch/ui";
import type {
  DuplicateCheckMatch,
  SelectableRequestPriority,
} from "../../../types/workRequest";
import { PrioritySuggestion } from "./PrioritySelector";
import { RequestPriorityChip } from "./RequestPriorityChip";

type RequestAssessmentProps = {
  asset: string | null;
  /** Files staged to upload with the request. */
  attachments: Array<{ id: string; name: string }>;
  canSubmit: boolean;
  description: string | null;
  /** The pre-submit duplicate check failed, so an empty match list means
   * "unknown" rather than "no duplicates". */
  duplicateCheckUnavailable?: boolean;
  isPending: boolean;
  matches: DuplicateCheckMatch[];
  onDiscard: () => void;
  onEdit: () => void;
  onSubmit: () => void;
  priority: SelectableRequestPriority | null;
  suggestedPriority: SelectableRequestPriority | null;
  title: string | null;
};

const SUBMIT_LABEL = "Submit for Review";

// Identity-stable callback ref: React re-runs inline ref callbacks on every
// commit, which would steal focus back to the heading on each re-render.
function focusOnMount(node: HTMLElement | null) {
  node?.focus();
}

const monoClassName =
  "font-(family-name:--font-family-mono) text-(length:--font-size-label-sm) leading-(--font-line-height-16)";

// A request that was approved into a Work Order arrives as a WORK_ORDER match,
// so each match shows a single record number.
function MatchRow({ match }: { match: DuplicateCheckMatch }) {
  return (
    <li className="flex flex-col gap-(--spacing-4) px-(--spacing-12) py-(--spacing-8)">
      <span className={`${monoClassName} text-text-subtle`}>
        {match.displayId}
      </span>
      <Text weight="semibold">{match.title}</Text>
      {match.asset ? (
        <span className={`${monoClassName} text-primary`}>
          {match.asset.assetCode ?? match.asset.name}
        </span>
      ) : null}
      {match.description ? (
        <div className="whitespace-pre-line">
          <Text size="sm" tone="subtle">
            {match.description}
          </Text>
        </div>
      ) : null}
    </li>
  );
}

function DuplicateMatches({ matches }: { matches: DuplicateCheckMatch[] }) {
  return (
    /* @ds-deviation: no DS component lists several duplicate candidates in one advisory (Alert holds a single message), so this composes the warning container with divided candidate rows from tokens — promote a "duplicate candidates" pattern into the design system */
    <div className="flex shrink-0 flex-col gap-(--spacing-8) rounded-(--radius-md) border border-warning bg-warning-container p-(--spacing-12)">
      <div className="flex items-center gap-(--spacing-8) text-warning-on-container">
        <Icon name="triangle-exclamation-solid" size="xs" tone="inherit" />
        <Text as="span" size="sm" tone="inherit" weight="semibold">
          Possible duplicate
        </Text>
      </div>
      <ul className="m-0 list-none divide-y divide-outline-subtle overflow-hidden rounded-(--radius-sm) border border-outline-subtle bg-surface p-0">
        {matches.map((match) => (
          <MatchRow key={`${match.type}-${match.id}`} match={match} />
        ))}
      </ul>
    </div>
  );
}

function AssessmentField({
  children,
  label,
}: {
  children: React.ReactNode;
  label: string;
}) {
  return (
    <div className="flex flex-col gap-(--spacing-8) border-b border-outline-subtle px-(--spacing-16) py-(--spacing-12) last:border-b-0">
      <dt>
        <Label size="sm" tone="subtle">
          {label}
        </Label>
      </dt>
      <dd className="m-0">{children}</dd>
    </div>
  );
}

function AssessmentFooter({
  canSubmit,
  hasDuplicates,
  isPending,
  onDiscard,
  onEdit,
  onSubmit,
}: Pick<
  RequestAssessmentProps,
  "canSubmit" | "isPending" | "onDiscard" | "onEdit" | "onSubmit"
> & { hasDuplicates: boolean }) {
  return (
    <DrawerFooter>
      <div className="flex w-full flex-col gap-(--spacing-8)">
        {hasDuplicates ? (
          /* @ds-deviation: DS Button has no outline+destructive variant, so Discard Request uses ghost+destructive (red text, no red border) instead of the design reference's red outline — promote an outline+destructive Button variant into the design system */
          <Button
            disabled={isPending}
            fullWidth
            icon="x-close"
            intent="destructive"
            onClick={onDiscard}
            variant="ghost"
          >
            Discard Request
          </Button>
        ) : null}
        <Button
          disabled={isPending || !canSubmit}
          fullWidth
          icon="check-mark"
          loading={isPending}
          loadingText="Submitting"
          onClick={onSubmit}
          variant={hasDuplicates ? "outline" : "primary"}
        >
          {SUBMIT_LABEL}
        </Button>
        <Button
          disabled={isPending}
          fullWidth
          icon="edit-pen"
          onClick={onEdit}
          variant="outline"
        >
          Edit
        </Button>
      </div>
    </DrawerFooter>
  );
}

export function RequestAssessment({
  asset,
  attachments,
  canSubmit,
  description,
  duplicateCheckUnavailable = false,
  isPending,
  matches,
  onDiscard,
  onEdit,
  onSubmit,
  priority,
  suggestedPriority,
  title,
}: RequestAssessmentProps) {
  const hasDuplicates = matches.length > 0;
  const showUnavailableNotice = duplicateCheckUnavailable && !hasDuplicates;

  return (
    <>
      <div className="flex shrink-0 items-center gap-(--spacing-12) border-b border-outline-subtle p-(--spacing-16)">
        <span className="inline-flex size-(--spacing-32) shrink-0 items-center justify-center rounded-(--radius-md) bg-surface-container-high">
          <Icon name="sparkles" size="sm" tone="secondary" />
        </span>
        <span className="flex min-w-0 flex-col gap-(--spacing-4)">
          <span className="outline-none" ref={focusOnMount} tabIndex={-1}>
            <Text as="span" weight="semibold">
              Reli&apos;s assessment
            </Text>
          </span>
          <Text as="span" size="sm" tone="subtle">
            {hasDuplicates
              ? "Possible duplicate detected"
              : showUnavailableNotice
                ? "Duplicate check unavailable"
                : "Ready to submit"}
          </Text>
        </span>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-(--spacing-16) overflow-y-auto bg-surface-container-low p-(--spacing-16)">
        {hasDuplicates ? <DuplicateMatches matches={matches} /> : null}
        {showUnavailableNotice ? (
          <Alert
            subtext="You can still submit this Request."
            title="The duplicate check could not run."
            tone="warning"
          />
        ) : null}

        <div className="flex shrink-0 flex-col gap-(--spacing-8)">
          <Label size="sm" tone="subtle">
            REQUEST DETAILS
          </Label>
          <dl className="m-0 overflow-hidden rounded-(--radius-lg) border border-outline-subtle bg-surface">
            <AssessmentField label="TITLE">
              <Text weight="semibold">{title}</Text>
            </AssessmentField>
            <AssessmentField label="DESCRIPTION">
              <div className="whitespace-pre-line">
                <Text tone="subtle">{description}</Text>
              </div>
            </AssessmentField>
            <AssessmentField label="ASSET">
              <span className={`${monoClassName} text-primary`}>{asset}</span>
            </AssessmentField>
            <AssessmentField label="PRIORITY">
              <div className="flex flex-col items-start gap-(--spacing-4)">
                {priority ? (
                  <RequestPriorityChip priority={priority} />
                ) : (
                  <Text tone="subtle">None</Text>
                )}
                {suggestedPriority ? (
                  <PrioritySuggestion value={suggestedPriority} />
                ) : null}
              </div>
            </AssessmentField>
            <AssessmentField label="ATTACHMENTS">
              {attachments.length > 0 ? (
                <ul className="m-0 flex list-none flex-col gap-(--spacing-4) p-0">
                  {attachments.map(({ id, name }) => (
                    <li
                      className="flex min-w-0 items-center gap-(--spacing-8) text-text-subtle"
                      key={id}
                    >
                      <Icon name="document" size="xs" tone="inherit" />
                      <Text as="span" truncate>
                        {name}
                      </Text>
                    </li>
                  ))}
                </ul>
              ) : (
                <Text tone="subtle">None</Text>
              )}
            </AssessmentField>
          </dl>
        </div>
      </div>

      <AssessmentFooter
        canSubmit={canSubmit}
        hasDuplicates={hasDuplicates}
        isPending={isPending}
        onDiscard={onDiscard}
        onEdit={onEdit}
        onSubmit={onSubmit}
      />
    </>
  );
}
